import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import {
  GetDisplayDate,
  GetTimeSpan,
} from '../hooks/timestamp'
import { useWorkouts } from '../providers/workouts'
import { GetImage } from '../assets/workout-image-provider'
import AppHeader from '../components/app-header'

import {
  People,
  PermIdentity,
  LocationOn,
  AccessTime,
  Info,
  Backpack,
  FiberManualRecord,
  AccountBalanceWallet,
  HourglassEmpty,
} from '@mui/icons-material'
import { useAuth } from '../core/providers/auth'
import { useEffect, useState } from 'react'
import { firestore } from '../core'
import ParticipantsList from '../components/participants-list'
import Loading from '../core/views/loading'
import { useWorkoutSettings } from '../providers/workout-settings'
import {
  BookingError,
  claimWorkoutWaitlistSpot,
  joinWorkoutWaitlist,
  leaveWorkoutWaitlist,
  toggleWorkoutBooking,
} from '../lib/booking'
import { features } from '../core/config/features'
import WaitlistList from '../components/waitlist-list'

type Props = {
  window?: () => Window
}

function getLength(start: Date, end: Date) {
  var diff = (end.getTime() - start.getTime()) / 1000
  diff /= 60
  return Math.abs(Math.round(diff))
}

export default function DetailedWorkout({ window }: Props) {
  const { workouts } = useWorkouts()
  const { getWorkoutType, getWorkoutTypeName, isWorkoutTypeBookable } =
    useWorkoutSettings()
  const { id } = useParams()
  const [workout] = workouts.filter((w) => w.id === id).map((w, key) => w)
  const { user, admin, coach } = useAuth()
  const [loading, setLoading] = useState(false)
  const [inParticipantsList, setInParticipantsList] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const workoutType = workout ? getWorkoutType(workout.type) : null
  const isTypeBookable = workout
    ? isWorkoutTypeBookable(workout.type)
    : false
  const workoutCredits = features.credits ? Number(workout?.credits ?? 0) : 0
  const userCredits = Number(user?.credits ?? 0)
  const waitlist = workout?.waitlist ?? []
  const waitlistPosition = user
    ? waitlist.findIndex((entry) => entry.uid === user.uid) + 1
    : 0
  const inWaitlist = waitlistPosition > 0
  const canSeeFullWaitlist = admin || coach
  const spotsAvailable = workout
    ? workout.participantsCount < workout.numberOfPlaces
    : false
  const waitlistClosesAt = workout
    ? new Date(workout.startTime.toDate().getTime() - 2 * 60 * 60 * 1000)
    : null
  const isWaitlistOpen = waitlistClosesAt ? new Date() < waitlistClosesAt : false
  const canClaimWaitlistSpot =
    canSeeFullWaitlist && inWaitlist && spotsAvailable && waitlistPosition === 1
  const hasEnoughCredits =
    !user || inParticipantsList || inWaitlist || workoutCredits <= userCredits

  const needsCreditsForPrimaryAction =
    !inParticipantsList && (!inWaitlist || canClaimWaitlistSpot)
  const hasEnoughCreditsForPrimaryAction =
    !needsCreditsForPrimaryAction || workoutCredits <= userCredits

  const primaryButtonLabel = (() => {
    if (inParticipantsList) return 'avboka'
    if (canClaimWaitlistSpot) return 'boka plats'
    if (inWaitlist) return 'lämna kö'
    if (!spotsAvailable && isWaitlistOpen) return 'köa'
    if (!spotsAvailable) return 'kö stängd'
    return 'boka'
  })()

  const primaryButtonDisabled =
    !isTypeBookable ||
    (!inParticipantsList && !hasEnoughCreditsForPrimaryAction) ||
    (!inParticipantsList && !inWaitlist && !spotsAvailable && !isWaitlistOpen)

  const handleCheck = async () => {
    if (!workout || !user) {
      setBookingError('Du behöver vara inloggad för att boka pass.')
      return
    }

    if (!isTypeBookable) return
    if (!inParticipantsList && !hasEnoughCredits) {
      setBookingError(
        `Du behöver ${workoutCredits} credits för att boka passet. Ditt saldo är ${userCredits}.`
      )
      return
    }

    setLoading(true)
    setBookingError(null)

    try {
      await toggleWorkoutBooking(firestore, workout.id, user, {
        creditsEnabled: features.credits,
        participantsCount: workout.participantsCount,
        numberOfPlaces: workout.numberOfPlaces,
      })
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att uppdatera bokningen. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleWaitlist = async () => {
    if (!workout || !user) {
      setBookingError('Du behöver vara inloggad för att köa till pass.')
      return
    }

    if (!isTypeBookable) return
    if (!inWaitlist && !hasEnoughCreditsForPrimaryAction) {
      setBookingError(
        `Du behöver ${workoutCredits} credits för att köa till passet. Ditt saldo är ${userCredits}.`
      )
      return
    }

    if (!inWaitlist && !isWaitlistOpen) {
      setBookingError('Kön är stängd eftersom det är mindre än två timmar kvar.')
      return
    }

    setLoading(true)
    setBookingError(null)

    try {
      if (inWaitlist) {
        await leaveWorkoutWaitlist(firestore, workout.id, user)
      } else {
        await joinWorkoutWaitlist(firestore, workout.id, user, {
          creditsEnabled: features.credits,
        })
      }
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att uppdatera kön. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClaimWaitlistSpot = async () => {
    if (!workout || !user) {
      setBookingError('Du behöver vara inloggad för att boka pass.')
      return
    }

    if (!hasEnoughCreditsForPrimaryAction) {
      setBookingError(
        `Du behöver ${workoutCredits} credits för att boka passet. Ditt saldo är ${userCredits}.`
      )
      return
    }

    setLoading(true)
    setBookingError(null)

    try {
      await claimWorkoutWaitlistSpot(firestore, workout.id, user, {
        creditsEnabled: features.credits,
        participantsCount: workout.participantsCount,
        numberOfPlaces: workout.numberOfPlaces,
        waitlistPosition,
      })
    } catch (error) {
      console.log(error)
      setBookingError(
        error instanceof BookingError
          ? error.message
          : 'Det gick inte att boka den lediga platsen. Försök igen om en stund.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handlePrimaryAction = () => {
    if (canClaimWaitlistSpot) {
      handleClaimWaitlistSpot()
      return
    }

    if (inWaitlist || (!spotsAvailable && !inParticipantsList)) {
      handleWaitlist()
      return
    }

    handleCheck()
  }

  useEffect(() => {
    if (!workout || !user) return

    const arr = workout.participantsList.map(({ uid }) => uid)
    arr.includes(user.uid)
      ? setInParticipantsList(true)
      : setInParticipantsList(false)
    setLoading(false)
    // eslint-disable-next-line
  }, [workout?.participantsList, user?.uid])

  if (!workout) {
    return <Loading />
  }

  return (
    <Box sx={{ maxWidth: '25rem', margin: '0 auto' }}>
      <AppHeader path={`/workout/${id}`} workout={workout} />
      <Box
        sx={{
          maxWidth: '100%',
        }}
      >
        <Box
          sx={{
            maxWidth: '100%',
            marginTop: '2rem',
            display: 'flex',
            justifyContent: 'start',
          }}
        >
          <Box
            sx={{
              backgroundImage: `url(${workoutType?.image ?? GetImage(workout.type)})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: '100% 100%',
              width: '100%',
              height: '300px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                color: 'white',
              }}
            >
              <Typography
                sx={{
                  display: 'flex',
                  flexGrow: 1,
                  fontWeight: 'bold',
                  fontSize: '3rem',
                  margin: '6.3rem auto',
                  color: 'White',
                }}
              >
                {getWorkoutTypeName(workout.type)}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            margin: '1rem 0 0 1.5rem',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              marginTop: '.2rem',
            }}
          >
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
              {GetDisplayDate(workout.startTime.toDate(), true)}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.3rem',
                fontWeight: 'bold',
                marginBottom: '1rem',
              }}
            >
              {GetTimeSpan(workout.startTime, workout.endTime)}
            </Typography>
          </Box>
          <Box>
            {loading ? (
              <CircularProgress
                sx={{
                  margin: '0 2.5rem 0 0',
                }}
              />
            ) : !isTypeBookable ? (
              <Button
                disabled
                variant="outlined"
                sx={{
                  fontSize: '0.85rem',
                  margin: '0 1rem 0 0',
                  width: '8rem',
                  height: '2.5rem',
                }}
              >
                Abonnerat
              </Button>
            ) : primaryButtonDisabled ? (
              <Button
                disabled
                variant="outlined"
                sx={{
                  fontSize: '0.85rem',
                  margin: '0 1rem 0 0',
                  width: '8rem',
                  height: '2.5rem',
                }}
              >
                {!hasEnoughCreditsForPrimaryAction
                  ? 'Saknar credits'
                  : primaryButtonLabel}
              </Button>
            ) : (
              <Button
                disableRipple
                onClick={handlePrimaryAction}
                variant={inParticipantsList || inWaitlist ? 'outlined' : 'contained'}
                sx={{
                  fontSize: primaryButtonLabel.length > 7 ? '0.85rem' : '1rem',
                  margin: '0 1rem 0 0',
                  width: primaryButtonLabel.length > 7 ? '7.5rem' : '6rem',
                  height: '2.5rem',
                }}
              >
                {primaryButtonLabel}
              </Button>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexGrow: 1,
            marginLeft: '1.5rem',
          }}
        >
          <List dense disablePadding>
            <ListItem disablePadding sx={{ margin: 0 }}>
              <ListItemButton sx={{ padding: 0 }}>
                <ListItemIcon sx={{ minWidth: '2rem' }}>
                  <AccessTime />
                </ListItemIcon>
                <ListItemText
                  sx={{ fontSize: '1.2rem' }}
                  disableTypography
                  primary={`${getLength(
                    workout.startTime.toDate(),
                    workout.endTime.toDate()
                  )}min`}
                />
              </ListItemButton>
            </ListItem>
            {workout.collectionTime > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <FiberManualRecord />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={`Samling ${workout.collectionTime}min innan start`}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {workout.registrationTime > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <FiberManualRecord />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={`Boka senast ${workout.registrationTime}h innan start`}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {isTypeBookable && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <People />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={`${workout.participantsCount}/${workout.numberOfPlaces} deltagare`}
                  />
                </ListItemButton>
              </ListItem>
            )}
            {isTypeBookable && workoutCredits > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <AccountBalanceWallet />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={
                      user
                        ? `Kostar ${workoutCredits} credits. Ditt saldo är ${userCredits}.`
                        : `Kostar ${workoutCredits} credits`
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {isTypeBookable && workout.waitlistCount > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <HourglassEmpty />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={
                      inWaitlist && canSeeFullWaitlist
                        ? `Kö: ${workout.waitlistCount}. Din plats: ${waitlistPosition}.`
                        : inWaitlist
                        ? 'Du står i kön'
                        : `Kö: ${workout.waitlistCount}`
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            <ListItem disablePadding>
              <ListItemButton sx={{ padding: 0 }}>
                <ListItemIcon sx={{ minWidth: '2rem' }}>
                  <PermIdentity />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.2rem' }}
                  primary={
                    workout.coaches?.length > 0 ? (
                      <>
                        Coach:{' '}
                        {workout.coaches.map((c, i) => (
                          <span key={i}>
                            {c.displayName}
                            {i + 1 !== workout.coaches.length && ','}{' '}
                          </span>
                        ))}
                      </>
                    ) : (
                      'Ej angivet'
                    )
                  }
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton sx={{ padding: 0 }}>
                <ListItemIcon sx={{ minWidth: '2rem' }}>
                  <LocationOn />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.2rem' }}
                  primary={workout.location}
                />
              </ListItemButton>
            </ListItem>
            {workout.gear.length > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <Backpack />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={
                      workout.gear.length > 0 && (
                        <>
                          Ta med{' '}
                          {workout.gear.map((g, index) => {
                            return (
                              <span key={index}>
                                {g}
                                {index + 1 !== workout.gear.length && ','}{' '}
                              </span>
                            )
                          })}{' '}
                        </>
                      )
                    }
                  />
                </ListItemButton>
              </ListItem>
            )}
            {workout.body.length > 0 && (
              <ListItem disablePadding>
                <ListItemButton sx={{ padding: 0 }}>
                  <ListItemIcon sx={{ minWidth: '2rem' }}>
                    <Info />
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ fontSize: '1.2rem' }}
                    primary={workout.body}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </List>
        </Box>

        {isTypeBookable && (
          <Box sx={{ margin: '1.5rem 0 0 2rem' }}>
            {inWaitlist && (
              <Alert severity="info" sx={{ margin: '0 0 1rem -0.5rem' }}>
                {canSeeFullWaitlist
                  ? `Du står som nummer ${waitlistPosition} i kön. En ledig plats kan tas av den som står först i kön.`
                  : 'Du står i kön. Coach eller administratör flyttar in deltagare när en plats blir ledig.'}
              </Alert>
            )}
            <Divider variant="fullWidth" sx={{ margin: '0.5rem 0 1rem 0' }} />
            <ParticipantsList workout={workout} />
            <WaitlistList workout={workout} />
          </Box>
        )}
      </Box>
      <Snackbar
        open={!!bookingError}
        autoHideDuration={6000}
        onClose={() => setBookingError(null)}
      >
        <Alert severity="error" onClose={() => setBookingError(null)}>
          {bookingError}
        </Alert>
      </Snackbar>
    </Box>
  )
}
